import React, { useEffect, useState } from 'react';
import Yup from '~yup';
import { useFormik } from 'formik';

import Text from '~components/atoms/Text';
import Checkbox from '~components/atoms/Checkbox';
import ActionButton from '~components/atoms/ActionButton';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import { TDropdownOption } from '~types/components/labelledDropdown';
import { TInventoryFilter, TTransferInventoryParams } from '~types/feat/gold';
import { BranchStatusEnum, GoldTypeEnum } from '~utils/enums';

import * as FormStyles from './_transferInventoryForm.module.scss';

// ? validation
export const getTransferInventorySchema = (
    branchesList: TDropdownOption[],
    selectedFromBranch?: string,
    selectedToBranch?: string,
) => Yup.object().shape({
    branchFromCode: Yup.string().required('Current Branch Code is required'),
    branchFromName: Yup.string()
        .matchNoLeadingAndTrailingWhiteSpaces()
        .matchMaxOneWhiteSpace()
        .required('Current Branch name is required')
        .test('check-branch-name',
            'Selected branch does not exist',
            function (value) {
                if (!value) return false;
                return branchesList.some((branch) => branch.label === value);
            }
        ),
    branchToCode: Yup.string().required('Target Branch Code is required'),
    branchToName: Yup.string()
        .matchNoLeadingAndTrailingWhiteSpaces()
        .matchMaxOneWhiteSpace()
        .required('Target Branch name is required')
        .test('check-branch-name',
            'Selected branch does not exist',
            function (value) {
                if (!value) return false;
                return branchesList.some((branch) => branch.label === value);
            }
        )
        // .test(
        //     'check-inventory',
        //     'Selected product does not exist in the target branch',
        //     function () {
        //         if (!selectedProduct) return false;
        //         if (!branchToInventory) return false;
        //         return branchToInventory.some((inventory) => inventory.productId === selectedProduct.productId);
        //     }
        // )
        .test(
            'check-branch',
            'Cannot transfer to the same branch',
            function () {
                if (!selectedFromBranch) return false;
                if (!selectedToBranch) return false;
                return selectedFromBranch !== selectedToBranch;
            }
        ),
    productId: Yup.string().required('ProductID is required'),
    productName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Product Name is required'),
    amount: Yup.number().typeError('Quantity must be a number').positive('Quantity must be positive').required('Amount is required'),
    serialNo: Yup.array().of(Yup.string()),
});

const TransferInventoryForm = () => {

    const dispatch = useAppDispatch();

    const branchesList = useAppSelector((state) => state.branch.allBranches);
    const inventoryList = useAppSelector((state) => state.gold.allInventories);
    const serialNumberList = useAppSelector((state) => state.gold.allSerialNumbers);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const inventoryFilterParams = useAppSelector((state) => state.gold.params.inventoryFilterParams);
    const serialNumberFilterParams = useAppSelector((state) => state.gold.params.serialNumberFilterParams);

    const transferInventoryAttempt = useAppSelector((state) => state.gold.loadings.transferInventory);

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map((branch) => ({
        label: branch.branchName,
        value: branch.branchCode,
    }));

    const transferToBranchOptions = branchesList.filter(branch => branch.branchCode !== branchCode && branch.status === BranchStatusEnum.Active).map((branch) => ({
        label: branch.branchName,
        value: branch.branchCode,
    }));

    const inventoryOptions = inventoryList && inventoryList.filter(inventory => inventory.goldType === GoldTypeEnum.PhysicalGold ).map((inventory) => ({
        label: inventory.productName,
        value: inventory.productId,
    }));

    const TransferInventoryInitialValues = {
        branchFromCode: allBranchAccess ? '' : (branchesList?.find(branch => branch.branchCode === branchCode)?.branchCode || ''),
        branchFromName: allBranchAccess ? '' : (branchesList?.find(branch => branch.branchCode === branchCode)?.branchName || ''),
        branchToCode: '',
        branchToName: '',
        productId: 0,
        productName: '',
        amount: '',
        serialNo: [] as string[],
    };

    const [selectedBranchFromCode, setSelectedBranchFromCode] = useState<string>('');
    const [selectedBranchToCode, setSelectedBranchToCode] = useState<string>('');
    const [selectedProduct, setSelectedProduct] = useState<TInventoryFilter>();
    const [selectedSerialNumber, setSelectedSerialNumber] = useState<string[]>([]);

    const formik = useFormik({
        initialValues: TransferInventoryInitialValues,
        validationSchema: getTransferInventorySchema(branchOptions, selectedBranchFromCode, selectedBranchToCode),
        onSubmit: values => {
            handleOnSubmit(values);
        },
    });

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess){

            const lockedBranch = branchesList.find(branch => branch.branchCode === branchCode);
            if (lockedBranch) {
                const branch = {
                    value: lockedBranch.branchCode,
                    label: lockedBranch.branchName,
                };
                formik.setFieldValue('branchFromCode', lockedBranch?.branchCode);
                handleOnSelectBranchFrom(branch);
                formik.setFieldValue('branchFromName', lockedBranch?.branchName);
            }
        }
    }, [branchesList]);

    useEffect(() => {
        if (selectedBranchFromCode) {
            dispatch(combinedActions.goldSetInventoryFilterParams({ ...inventoryFilterParams, branchCode: selectedBranchFromCode }));
            dispatch(combinedActions.goldSetSerialNumberFilterParams({ ...serialNumberFilterParams, branchCode: selectedBranchFromCode, action: 1 })); // TODO: later need to adjust params
            dispatch(combinedActions.goldGetAllInventoriesAttempt());
            setSelectedProduct(undefined);
            formik.setValues({
                ...formik.values,
                productId: 0,
                productName: '',
                amount: '',
                serialNo: [],
            }); // to reset value each time changing branch
        }
    }, [selectedBranchFromCode]);

    useEffect(() => {
        if (selectedBranchToCode) {
            dispatch(combinedActions.goldGetBranchInventoriesAttempt({ branchCode: selectedBranchToCode }));
            setSelectedProduct(undefined);
            formik.setValues({
                ...formik.values,
                productId: 0,
                productName: '',
                amount: '',
                serialNo: [],
            }); // to reset value each time changing branch
        }
    }, [selectedBranchToCode]);

    useEffect(() => {
        if (selectedProduct) {
            setSelectedSerialNumber([]);
            dispatch(combinedActions.goldSetSerialNumberFilterParams({ ...serialNumberFilterParams, productId: selectedProduct.productId }));
            dispatch(combinedActions.goldGetAllSerialNumbersAttempt());
            formik.setValues({
                ...formik.values,
                amount: '',
                serialNo: [],
            });
        }
    }, [selectedProduct]);

    useEffect(() => {
        if (selectedSerialNumber.length > 0) {
            formik.setValues({
                ...formik.values,
                amount: selectedSerialNumber.length.toString(),
                serialNo: selectedSerialNumber,
            });
        } else {
            formik.setValues({
                ...formik.values,
                amount: '',
                serialNo: [],
            });
        }
    }, [selectedSerialNumber]);

    useEffect(() => {
        // Note: Not batching this setFieldValue with branchName's onChange setFieldValue because of a race condition
        // the branchName validation will fire first before the update is done, rendering an error for the branchName field
        const newBranch = branchesList.find(branch => branch.branchName === formik.values.branchFromName);

        if (newBranch) {
            formik.setFieldValue('branchFromCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchFromCode', '');
        }
    }, [formik.values.branchFromName]);

    useEffect(() => {
        // Note: Not batching this setFieldValue with branchName's onChange setFieldValue because of a race condition
        // the branchName validation will fire first before the update is done, rendering an error for the branchName field
        const newBranch = branchesList.find(branch => branch.branchName === formik.values.branchToName);

        if (newBranch) {
            formik.setFieldValue('branchToCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchToCode', '');
        }
    }, [formik.values.branchToName]);

    const handleOnSearchBranchFrom = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('branchFromName', event.target.value);
    };

    const handleOnSelectBranchFrom = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'branchFromCode', value: option.value } });
        setSelectedBranchFromCode(option.value as string);
    };

    const handleOnSearchBranchTo = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('branchToName', event.target.value);
    };

    const handleOnSelectBranchTo = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'branchToCode', value: option.value } });
        setSelectedBranchToCode(option.value as string);
    };

    const handleOnSearchProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'productName', value: event.target.value } });

        const product = inventoryList.find((productOption) => productOption.productName === event.target.value);

        if (!product) {
            setSelectedProduct(undefined);
            return;
        }
        setSelectedProduct(product);
        formik.setValues({
            ...formik.values,
            productId: Number(product.productId),
            productName: product.productName,
        });
    };

    const handleOnSelectProduct = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'productName', value: option.value } });

        const product = inventoryList.find((productOption) => productOption.productId === option.value);

        if (!product) {
            setSelectedProduct(undefined);
            return;
        }
        setSelectedProduct(product);
        formik.setValues({
            ...formik.values,
            productId: Number(product.productId),
            productName: product.productName,
        });
    };

    const handleOnSelectSerialNumber = (serialNumber: string) => {
        if (selectedSerialNumber.includes(serialNumber)) {
            setSelectedSerialNumber(selectedSerialNumber.filter((item) => item !== serialNumber));
        } else {
            setSelectedSerialNumber([...selectedSerialNumber, serialNumber]);
        }
    };

    const handleOnSelectAllSerialNumber = () => {
        if (selectedSerialNumber.length === serialNumberList.length) {
            setSelectedSerialNumber([]);
        } else {
            setSelectedSerialNumber(serialNumberList.map((serialNumber) => serialNumber.serialNo));
        }
    };

    const handleOnSubmit = (data: TTransferInventoryParams) => {
        dispatch(combinedActions.goldSetTransferInventoryParams(data));
        dispatch(combinedActions.goldTransferInventoryAttempt());
    };

    const renderSerialNumbers = () => {
        if (serialNumberList && serialNumberList.length > 0) {
            return serialNumberList.map((serialNumber) => {
                return (
                    <div className={FormStyles['serial-number']} key={serialNumber.itemCode}>
                        <Checkbox
                            isReversed
                            label={serialNumber.serialNo}
                            isChecked={selectedSerialNumber.includes(serialNumber.serialNo)}
                            onChange={() => handleOnSelectSerialNumber(serialNumber.serialNo)}
                        />
                    </div>
                );
            });
        }
    };

    const renderSerialNumberList = () => {
        if (selectedProduct && selectedProduct.goldType === GoldTypeEnum.PhysicalGold) {
            return (
                <div className={FormStyles['serial-container']}>
                    <Text
                        isBold
                        label={'Serial Numbers* :'}
                        className={FormStyles['title']}
                    />

                    <div className={FormStyles['serial-list']}>
                        <Checkbox
                            isReversed
                            label='Select All'
                            isChecked={selectedSerialNumber.length === serialNumberList.length && serialNumberList.length > 0}
                            onChange={handleOnSelectAllSerialNumber}
                        />

                        {renderSerialNumbers()}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
        >
            <div className={FormStyles['form-container']}>

                <LabelledDropdown
                    id='branchFromName'
                    name='branchFromName'
                    label='Current Branch* :'
                    options={branchOptions}
                    value={formik.values.branchFromName}
                    error={formik.touched.branchFromName ? formik.errors.branchFromName : undefined}
                    onChange={handleOnSearchBranchFrom}
                    onSelectOption={handleOnSelectBranchFrom}
                    onBlur={formik.handleBlur}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={!!branchOptions.find((branch) => branch.value === formik.values.branchFromCode)}
                    disabled={!allBranchAccess}
                />

                <LabelledDropdown
                    id='branchToName'
                    name='branchToName'
                    label='Target Branch* :'
                    options={transferToBranchOptions}
                    value={formik.values.branchToName}
                    error={formik.touched.branchToName ? formik.errors.branchToName : undefined}
                    onChange={handleOnSearchBranchTo}
                    onSelectOption={handleOnSelectBranchTo}
                    onBlur={formik.handleBlur}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={!!transferToBranchOptions.find((branch) => branch.value === formik.values.branchToCode)}
                    disabled={!selectedBranchFromCode}
                />

                <LabelledDropdown
                    id='productName'
                    name='productName'
                    label='Product Name* :'
                    options={inventoryOptions}
                    value={formik.values.productName}
                    error={formik.touched.productName ? formik.errors.productName : undefined}
                    onChange={handleOnSearchProduct}
                    onSelectOption={handleOnSelectProduct}
                    onBlur={formik.handleBlur}
                    noOptionsMessage='No product found'
                    placeholder='Select current branch first'
                    autoComplete='off'
                    optionSelected={!!inventoryOptions.find((product) => product.label === formik.values.productName)}
                    disabled={!selectedBranchFromCode}
                />

                <LabelledInput
                    id='amount'
                    name='amount'
                    label='Quantity* :'
                    value={formik.values.amount}
                    error={formik.touched.amount ? formik.errors.amount : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!selectedProduct || selectedProduct.goldType === GoldTypeEnum.PhysicalGold}
                />
            </div>

            {renderSerialNumberList()}

            <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                <div className={FormStyles['button-subcontainer']}>
                    <ActionButton
                        label='Transfer Inventory'
                        type='submit'
                        isLoading={transferInventoryAttempt}
                        disabled={transferInventoryAttempt}
                    />

                    {/* <Text
                        label={transferInventoryError}
                        isError
                    /> */}
                </div>
            </div>
        </form>
    );
};

export default TransferInventoryForm;
